import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";

interface Distribution {
 stakeholder: string;
 percentage: number;
}

interface ProfitRule {
 _id: string;
 name: string;
 conditions: {
  productType?: string;
  isCustomizable?: boolean;
  hasDesign?: boolean;
 };
 distributions: Distribution[];
 baseCost: number;
 createdAt: string;
}

export const ProfitRules: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [rules, setRules] = useState<ProfitRule[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showForm, setShowForm] = useState(false);
 const [editingId, setEditingId] = useState<string | null>(null);
 const [formData, setFormData] = useState({
  name: "",
  productType: "",
  isCustomizable: false,
  hasDesign: false,
  baseCost: 0,
  distributions: [{ stakeholder: "", percentage: 0 }],
 });

 useEffect(() => {
  fetchRules();
 }, []);

 useEffect(() => {
  if (showForm && !editingId) {
   // Reset form when opening modal for new rule
   setFormData({
    name: "",
    productType: "",
    isCustomizable: false,
    hasDesign: false,
    baseCost: 0,
    distributions: [{ stakeholder: "", percentage: 0 }],
   });
   setError(null);
  }
 }, [showForm, editingId]);

 const fetchRules = async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await api.get("/admin/profit-rules");
   setRules(response.data.rules || []);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.profitRules.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  // Validate distributions sum to 100
  const totalPercentage = formData.distributions.reduce(
   (sum, d) => sum + d.percentage,
   0
  );
  if (totalPercentage !== 100) {
   setError(t("admin.profitRules.errors.distributionSum"));
   return;
  }

  try {
   const payload = {
    name: formData.name,
    conditions: {
     productType: formData.productType || undefined,
     isCustomizable: formData.isCustomizable,
     hasDesign: formData.hasDesign,
    },
    distributions: formData.distributions.filter(
     (d) => d.stakeholder && d.percentage > 0
    ),
    baseCost: formData.baseCost,
   };

   if (editingId) {
    await api.patch(`/admin/profit-rules/${editingId}`, payload);
   } else {
    await api.post("/admin/profit-rules", payload);
   }

   resetForm();
   fetchRules();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.profitRules.errors.save"));
  }
 };

 const handleEdit = (rule: ProfitRule) => {
  setFormData({
   name: rule.name,
   productType: rule.conditions.productType || "",
   isCustomizable: rule.conditions.isCustomizable || false,
   hasDesign: rule.conditions.hasDesign || false,
   baseCost: rule.baseCost,
   distributions: rule.distributions,
  });
  setEditingId(rule._id);
  setShowForm(true);
 };

 const handleDelete = async (ruleId: string) => {
  if (!window.confirm(t("admin.profitRules.confirmDelete"))) {
   return;
  }

  try {
   await api.delete(`/admin/profit-rules/${ruleId}`);
   setRules(rules.filter((r) => r._id !== ruleId));
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.profitRules.errors.delete"));
  }
 };

 const resetForm = () => {
  setShowForm(false);
  setEditingId(null);
  setFormData({
   name: "",
   productType: "",
   isCustomizable: false,
   hasDesign: false,
   baseCost: 0,
   distributions: [{ stakeholder: "", percentage: 0 }],
  });
  setError(null);
 };

 const updateDistribution = (
  index: number,
  field: string,
  value: any
 ) => {
  const newDistributions = [...formData.distributions];
  newDistributions[index] = {
   ...newDistributions[index],
   [field]: value,
  };
  setFormData({ ...formData, distributions: newDistributions });
 };

 const addDistribution = () => {
  setFormData({
   ...formData,
   distributions: [...formData.distributions, { stakeholder: "", percentage: 0 }],
  });
 };

 const removeDistribution = (index: number) => {
  setFormData({
   ...formData,
   distributions: formData.distributions.filter((_, i) => i !== index),
  });
 };

 if (loading && rules.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">{t("admin.profitRules.title")}</Heading>
     <Button
      onClick={() => {
       setShowForm(!showForm);
       if (showForm) resetForm();
      }}
     >
      {showForm ? t("common.cancel") : t("admin.profitRules.addRule")}
     </Button>
    </div>

    <Modal
     isOpen={showForm}
     onClose={resetForm}
     title={editingId ? t("admin.profitRules.editRule") : t("admin.profitRules.newRule")}
     size="lg"
    >
     <form onSubmit={handleSubmit}>
      <Stack spacing="md">
       {error && <Alert variant="error">{error}</Alert>}
       <Input
          label={t("admin.profitRules.ruleName")}
          required
          value={formData.name}
          onChange={(e) =>
           setFormData({ ...formData, name: e.target.value })
          }
          placeholder={t("admin.profitRules.ruleNamePlaceholder")}
         />

         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
           label={t("admin.profitRules.productType")}
           value={formData.productType}
           onChange={(e) =>
            setFormData({ ...formData, productType: e.target.value })
           }
           placeholder={t("admin.profitRules.productTypePlaceholder")}
          />
          <Input
           label={t("admin.baseCost")}
           type="number"
           step="0.01"
           value={formData.baseCost}
           onChange={(e) =>
            setFormData({
             ...formData,
             baseCost: parseFloat(e.target.value),
            })
           }
          />
         </div>

         <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
           {t("admin.conditions")}
          </label>
          <div className="flex items-center gap-4">
           <label className="flex items-center gap-2">
            <input
             type="checkbox"
             checked={formData.isCustomizable}
             onChange={(e) =>
              setFormData({
               ...formData,
               isCustomizable: e.target.checked,
              })
             }
             className="w-4 h-4"
            />
            <span className="text-sm">{t("admin.customizable")}</span>
           </label>
           <label className="flex items-center gap-2">
            <input
             type="checkbox"
             checked={formData.hasDesign}
             onChange={(e) =>
              setFormData({
               ...formData,
               hasDesign: e.target.checked,
              })
             }
             className="w-4 h-4"
            />
            <span className="text-sm">{t("admin.hasDesign")}</span>
           </label>
          </div>
         </div>

         <div>
          <div className="flex items-center justify-between mb-2">
           <label className="block text-sm font-medium text-gray-700">
            {t("admin.profitDistribution")}
           </label>
           <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addDistribution}
           >
            {t("admin.addStakeholder")}
           </Button>
          </div>
          <div className="space-y-2">
           {formData.distributions.map((dist, index) => (
            <div key={index} className="flex gap-2 items-end">
             <Input
              placeholder={t("admin.profitRules.stakeholderPlaceholder")}
              value={dist.stakeholder}
              onChange={(e) =>
               updateDistribution(
                index,
                "stakeholder",
                e.target.value
               )
              }
              className="flex-1"
             />
             <Input
              placeholder={t("admin.percentage")}
              type="number"
              min="0"
              max="100"
              step="1"
              value={dist.percentage}
              onChange={(e) =>
               updateDistribution(
                index,
                "percentage",
                parseFloat(e.target.value)
               )
              }
              className="w-24"
             />
             <span className="text-sm text-gray-500">%</span>
             {formData.distributions.length > 1 && (
              <Button
               type="button"
               variant="outline"
               size="sm"
               onClick={() => removeDistribution(index)}
               className="text-red-600"
              >
               {t("admin.remove")}
              </Button>
             )}
            </div>
           ))}
           <div className="text-sm text-gray-500">
            {t("admin.total")}: {formData.distributions.reduce((sum, d) => sum + d.percentage, 0)}%
           </div>
          </div>
         </div>

         <div className="flex gap-2">
          <Button type="submit">
           {editingId ? t("common.update") : t("common.create")}
          </Button>
          <Button
           type="button"
           variant="outline"
           onClick={resetForm}
          >
           {t("common.cancel")}
          </Button>
         </div>
        </Stack>
       </form>
    </Modal>

    <Card padding="lg">
     <Stack spacing="md">
      <Heading size="lg">{t("admin.profitRules.title")}</Heading>
      {rules.length === 0 ? (
       <div className="text-center text-gray-500 py-8">
        {t("admin.profitRules.noRules")}
       </div>
      ) : (
       <div className="space-y-4">
        {rules.map((rule) => (
         <div key={rule._id} className="p-4 border border-gray-200 ">
          <div className="flex items-start justify-between mb-3">
           <div>
            <div className="font-medium text-gray-900">{rule.name}</div>
            <div className="text-sm text-gray-500">
             {t("admin.baseCost")}: ${rule.baseCost.toFixed(2)}
            </div>
           </div>
           <div className="flex gap-2">
            <Button
             variant="outline"
             size="sm"
             onClick={() => handleEdit(rule)}
            >
             {t("admin.edit")}
            </Button>
            <Button
             variant="outline"
             size="sm"
             onClick={() => handleDelete(rule._id)}
             className="text-red-600"
            >
             {t("admin.delete")}
            </Button>
           </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-3">
           <div>
            <div className="text-xs font-semibold text-gray-600 mb-2">
             {t("admin.conditions")}
            </div>
            <div className="text-sm text-gray-700 space-y-1">
             {rule.conditions.productType && (
              <div>{t("admin.profitRules.type")}: {rule.conditions.productType}</div>
             )}
             {rule.conditions.isCustomizable && (
              <div>✓ {t("admin.customizable")}</div>
             )}
             {rule.conditions.hasDesign && (
              <div>✓ {t("admin.hasDesign")}</div>
             )}
            </div>
           </div>
           <div>
            <div className="text-xs font-semibold text-gray-600 mb-2">
             {t("admin.profitDistribution")}
            </div>
            <div className="text-sm text-gray-700 space-y-1">
             {rule.distributions.map((dist, idx) => (
              <div key={idx}>
               {dist.stakeholder}: {dist.percentage}%
              </div>
             ))}
            </div>
           </div>
          </div>
         </div>
        ))}
       </div>
      )}
     </Stack>
    </Card>
   </Stack>
  </Container>
 );
};

export default ProfitRules;
